import { ReportColumnData } from '../api';
import { buildCsv, parseCsv } from '../common/export/csv';

export const ALL_COLUMNS_PRESET_ID = 'all';

/** Positions of a preset's columns in the report, in file order. */
export const presetColumnIndexes = (columns: ReportColumnData[], fieldKeys: string[]): number[] => {
  const keep = new Set(fieldKeys);
  return columns.reduce<number[]>((indexes, column, index) => {
    if (keep.has(column.key)) {
      indexes.push(index);
    }
    return indexes;
  }, []);
};

/**
 * Narrow a server-rendered report to a preset's columns.
 *
 * Columns are matched **by position** against the report's column list and never by header text,
 * because the headers are localized — matching on them would select different columns in Hebrew and
 * English. That makes this dependent on the column list and the CSV describing the same report, so
 * when the two disagree on width we hand back the untouched CSV rather than silently shifting
 * columns: too many is recoverable, wrong ones are not.
 *
 * An empty preset, or one whose keys no longer exist, also falls through untouched.
 */
export const applyPresetToCsv = (csv: string, columns: ReportColumnData[], fieldKeys: string[]): string => {
  const indexes = presetColumnIndexes(columns, fieldKeys);
  if (!indexes.length) {
    return csv;
  }

  const [headers = [], ...dataRows] = parseCsv(csv);
  if (headers.length !== columns.length) {
    return csv;
  }

  return buildCsv({
    headers: indexes.map((index) => headers[index] ?? ''),
    rows: dataRows.map((row) => indexes.map((index) => row[index] ?? '')),
  });
};

export enum ColumnGroup {
  BOOKING = 'booking',
  CUSTOMER = 'customer',
  PAYMENT = 'payment',
  DISCOUNT = 'discount',
  CUSTOM_DETAILS = 'customDetails',
}

export const COLUMN_GROUPS: ColumnGroup[] = [
  ColumnGroup.BOOKING,
  ColumnGroup.CUSTOMER,
  ColumnGroup.PAYMENT,
  ColumnGroup.DISCOUNT,
  ColumnGroup.CUSTOM_DETAILS,
];

const COLUMN_GROUP_BY_KEY: Record<string, ColumnGroup> = {
  first_name: ColumnGroup.CUSTOMER,
  last_name: ColumnGroup.CUSTOMER,
  email: ColumnGroup.CUSTOMER,
  phone: ColumnGroup.CUSTOMER,
  can_spam: ColumnGroup.CUSTOMER,
  returning_customer: ColumnGroup.CUSTOMER,
  returning_customer_from_order_id: ColumnGroup.CUSTOMER,
  referral_source: ColumnGroup.CUSTOMER,
  utm: ColumnGroup.CUSTOMER,
  comment: ColumnGroup.CUSTOMER,
  price: ColumnGroup.PAYMENT,
  price_after_discount: ColumnGroup.PAYMENT,
  voucher_id: ColumnGroup.PAYMENT,
  external_voucher_code: ColumnGroup.PAYMENT,
  extra_payment_lines: ColumnGroup.PAYMENT,
  extra_items: ColumnGroup.PAYMENT,
  extra_items_total: ColumnGroup.PAYMENT,
  discount_id: ColumnGroup.DISCOUNT,
  discount_type: ColumnGroup.DISCOUNT,
  discount_value: ColumnGroup.DISCOUNT,
  discount_code: ColumnGroup.DISCOUNT,
  discount_description: ColumnGroup.DISCOUNT,
};

const CUSTOM_DETAIL_KEY_PREFIXES = ['custom_detail:', 'custom_personal_detail:'];

/** The section a report column is listed under in the preset editor; anything unlisted is booking data. */
export const columnGroupOf = (key: string): ColumnGroup =>
  CUSTOM_DETAIL_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))
    ? ColumnGroup.CUSTOM_DETAILS
    : COLUMN_GROUP_BY_KEY[key] ?? ColumnGroup.BOOKING;
